import { useState, useEffect } from "react";
import axios from "axios";
import { Edit, Trash2, History, ChevronDown, ChevronUp, Info, Phone, Users2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import KontaktTaskList from "@/components/KontaktTaskList";
import { normalizeNochZuErledigen, DEFAULT_THEMATIK_OPTIONEN, DEFAULT_VERANSTALTUNG_OPTIONEN, calculateNextPackage } from "@/utils/kundenUtils";
import { userStorage } from '../../utils/userStorage';
const API = process.env.REACT_APP_BACKEND_URL || "http://localhost:8001";

export default function TeampartnerCard({ tp, produkte = [], alleSchulden = [], onEdit, onDelete, onPromote, onShowDownlineTree, isEditMode = false, onFieldClick, customThematikOptionen = [], customVeranstaltungOptionen = [], tempBezahlteSchulden = [], tempNeueSchulden = [], onPauseClick = null, onShowVergangeneThemen = null, onOpenZuErledigen = null, onToggleTask = null, onDeleteTask = null, defaultExpanded = false, showChevron = true, onBetreuungClick = null, onBetreuungToggle = null, onKeineBetreuung = null, onPayPerson = null, onShowTeilzahlungHistorie = null }) {
  const isPausiert = tp.pausiert === true;
  const [downlineCount, setDownlineCount] = useState(null);
  const [linieName, setLinieName] = useState("");
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [showContactInfo, setShowContactInfo] = useState(false);
  const isKaltkontakt = tp.typ === "kaltkontakt";
  // Lokale Telefonnummer aus localStorage – muss bei Änderung von tp.id neu gelesen
  // werden, sonst bleibt der State auf dem ersten Mount-Wert hängen, wenn React
  // dieselbe Komponenten-Instanz mit anderem tp wiederverwendet (z.B. Detail-Modal-
  // Swipe oder Listen-Reordering). Das hat dazu geführt, dass Telefonnummern auf
  // dem falschen Kontakt erschienen / verschwanden.
  const [localPhone, setLocalPhone] = useState('');
  useEffect(() => {
    try {
      const phones = JSON.parse(userStorage.getItem('burningteam_contact_phones') || '{}');
      setLocalPhone(phones[tp.id] || '');
    } catch { setLocalPhone(''); }
  }, [tp.id]);
  const [editingPhone, setEditingPhone] = useState(false);
  const [phoneInput,   setPhoneInput]   = useState('');

  const saveLocalPhone = (val) => {
    const trimmed = val.trim();
    try {
      const phones = JSON.parse(userStorage.getItem('burningteam_contact_phones') || '{}');
      if (trimmed) phones[tp.id] = trimmed; else delete phones[tp.id];
      userStorage.setItem('burningteam_contact_phones', JSON.stringify(phones));
    } catch {}
    setLocalPhone(trimmed);
    setEditingPhone(false);
  };
  // Im Edit-Modus immer alles anzeigen; chevron-Button wird ausgeblendet
  const showFull = isExpanded || isEditMode;

  useEffect(() => {
    if (tp.typ === "teampartner" && tp.id) {
      fetchDownlineCount();
    }
    if (tp.linie && tp.linie !== "erstlinie") {
      fetchLinieName();
    }
  }, [tp.id, tp.typ, tp.linie]);

  const fetchLinieName = async () => {
    try {
      const response = await axios.get(`${API}/api/teampartner/${tp.linie}`);
      setLinieName(response.data.name);
    } catch (error) {
      console.error("Fehler beim Laden des Linien-Names:", error);
      setLinieName("Unbekannt");
    }
  };

  const fetchDownlineCount = async () => {
    try {
      const response = await axios.get(`${API}/api/teampartner/${tp.id}/downline`);
      setDownlineCount(response.data);
    } catch (error) {
      console.error("Error fetching downline:", error);
    }
  };

  // Tage seit letztem Kontakt/Termin berechnen
  const getDaysSince = (date) => {
    if (!date) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const pastDate = new Date(date);
    pastDate.setHours(0, 0, 0, 0);
    const diffTime = Math.abs(today - pastDate);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Tage und Stunden bis zum nächsten Termin berechnen
  const getDaysAndHoursUntil = (date) => {
    if (!date) return null;
    const now = new Date();
    const futureDate = new Date(date);
    const diffTime = futureDate - now;
    if (diffTime < 0) {
      const absDiffTime = Math.abs(diffTime);
      const days = Math.floor(absDiffTime / (1000 * 60 * 60 * 24));
      const hours = Math.floor((absDiffTime % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      return { days: -days, hours, isPast: true };
    } else {
      const days = Math.floor(diffTime / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diffTime % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      return { days, hours, isPast: false };
    }
  };

  const daysSinceLastTermin = getDaysSince(tp.letzter_termin);
  const timeUntilNextTermin = getDaysAndHoursUntil(tp.naechster_termin);

  // Follow-up Countdown Berechnung
  const getFollowupStatus = () => {
    if (!tp.befoerderungs_datum) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const befoerderungsDate = new Date(tp.befoerderungs_datum);
    befoerderungsDate.setHours(0, 0, 0, 0);
    const daysSinceBefoerderung = Math.floor((today - befoerderungsDate) / (1000 * 60 * 60 * 24));
    let daysSinceEinnahme = 0;
    if (tp.einnahmegespraech_datum) {
      const einnahmeDate = new Date(tp.einnahmegespraech_datum);
      einnahmeDate.setHours(0, 0, 0, 0);
      daysSinceEinnahme = Math.floor((today - einnahmeDate) / (1000 * 60 * 60 * 24));
    }
    const followups = [
      { days: 3, label: "3-5 Tage Einnahmegespräch", done: tp.einnahmegespraech_erledigt, basedOn: "befoerderung", daysSince: daysSinceBefoerderung, fieldName: "einnahmegespraech_erledigt" },
      { days: 7, label: "7 Tage Follow-up", done: tp.followup_3_5_erledigt, basedOn: "einnahme", daysSince: daysSinceEinnahme, needsEinnahme: true, fieldName: "followup_3_5_erledigt" },
      { days: 14, label: "14 Tage Follow-up", done: tp.followup_14_erledigt, basedOn: "einnahme", daysSince: daysSinceEinnahme, needsEinnahme: true, fieldName: "followup_14_erledigt" },
      { days: 21, label: "21 Tage Follow-up", done: tp.followup_30_erledigt, basedOn: "einnahme", daysSince: daysSinceEinnahme, needsEinnahme: true, fieldName: "followup_30_erledigt" },
      { days: 28, label: "28 Tage Follow-up", done: tp.followup_28_erledigt, basedOn: "einnahme", daysSince: daysSinceEinnahme, needsEinnahme: true, fieldName: "followup_28_erledigt" },
      { days: 83, label: "Paket Erinnerung", done: tp.paket_erinnerung_erledigt, basedOn: "befoerderung", daysSince: daysSinceBefoerderung, isActive: daysSinceBefoerderung >= 83, fieldName: "paket_erinnerung_erledigt" }
    ];
    return { daysSinceBefoerderung, daysSinceEinnahme, followups, hasEinnahme: !!tp.einnahmegespraech_datum };
  };

  const followupStatus = getFollowupStatus();

  // Kontakt-Schwellenwerte aus localStorage lesen (mit Defaults)
  const _ct = (() => {
    try { return JSON.parse(userStorage.getItem('burningteam_contact_thresholds') || '{}'); } catch { return {}; }
  })();
  const yellowDays = _ct.yellowEnabled !== false ? (_ct.yellowDays ?? 30) : null;
  const redDays    = _ct.redEnabled   !== false ? (_ct.redDays   ?? 60) : null;

  // Zuletzt kontaktiert Formatierung
  let lastContactColor = "bg-green-50 border-green-200 text-green-700";
  let lastContactIcon = "🟢";
  let lastContactText = "Kürzlich";
  if (daysSinceLastTermin !== null) {
    if (redDays !== null && daysSinceLastTermin > redDays) {
      lastContactColor = "bg-red-50 border-red-200 text-red-700";
      lastContactIcon = "🔴";
      lastContactText = `vor ${daysSinceLastTermin} Tagen`;
    } else if (yellowDays !== null && daysSinceLastTermin > yellowDays) {
      lastContactColor = "bg-yellow-50 border-yellow-200 text-yellow-700";
      lastContactIcon = "🟡";
      lastContactText = `vor ${daysSinceLastTermin} Tagen`;
    } else {
      lastContactText = `vor ${daysSinceLastTermin} Tagen`;
    }
  }

  // Nächster Termin Formatierung
  let nextTerminColor = "bg-green-50 border-green-200 text-green-700";
  let nextTerminIcon = "📅";
  let nextTerminStatus = "";
  if (timeUntilNextTermin !== null) {
    const { days, hours, isPast } = timeUntilNextTermin;
    if (isPast) {
      nextTerminColor = "bg-red-50 border-red-200 text-red-700";
      nextTerminIcon = "🔴";
      if (days === 0) {
        nextTerminStatus = `Überfällig seit ${hours} Stunde(n)`;
      } else {
        nextTerminStatus = `Überfällig seit ${days} Tag(en) & ${hours}h`;
      }
    } else if (days === 0 && hours === 0) {
      nextTerminColor = "bg-blue-50 border-blue-200 text-blue-700";
      nextTerminIcon = "📅";
      nextTerminStatus = "Jetzt!";
    } else if (days === 0) {
      nextTerminColor = "bg-blue-50 border-blue-200 text-blue-700";
      nextTerminIcon = "🔵";
      nextTerminStatus = `Heute in ${hours} Stunde(n)`;
    } else if (days >= 1 && days <= 3) {
      nextTerminColor = "bg-orange-50 border-orange-200 text-orange-700";
      nextTerminIcon = "🟠";
      nextTerminStatus = `in ${days} Tag(en) & ${hours}h`;
    } else if (days >= 4 && days <= 7) {
      nextTerminColor = "bg-yellow-50 border-yellow-200 text-yellow-700";
      nextTerminIcon = "🟡";
      nextTerminStatus = `in ${days} Tag(en) & ${hours}h`;
    } else {
      nextTerminColor = "bg-green-50 border-green-200 text-green-700";
      nextTerminIcon = "🟢";
      nextTerminStatus = `in ${days} Tagen & ${hours}h`;
    }
  }

  return (
    <div
      data-tour="kontakt-karte"
      className={`bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 border-2 ${
        isEditMode
          ? "border-yellow-400 hover:border-yellow-500"
          : isKaltkontakt ? "border-slate-300 hover:border-slate-400" : "border-emerald-300 hover:border-emerald-400"
      } overflow-hidden group`}
    >
      {/* Top colored stripe */}
      <div className={`h-2 ${isKaltkontakt ? "bg-gradient-to-r from-slate-400 to-slate-500" : "bg-gradient-to-r from-emerald-500 to-teal-500"}`} />

      {/* Pause/Unpause Status Bar - nur im Edit-Modus */}
      {isEditMode && (
        <div className="bg-orange-100 border-b border-orange-300 px-3 py-2 flex items-center justify-between">
          <p className="text-xs font-semibold text-orange-700">
            {isPausiert ? "⏸️ Pausiert" : "🟢 Aktiv"}
          </p>
          {onPauseClick && (
            <button
              onClick={onPauseClick}
              className={`px-2 py-1 rounded text-xs font-semibold transition-colors ${
                isPausiert
                  ? "bg-green-600 text-white hover:bg-green-700"
                  : "bg-orange-600 text-white hover:bg-orange-700"
              }`}
            >
              {isPausiert ? "▶️ Fortsetzen" : "⏸️ Pausieren/Kündigen"}
            </button>
          )}
        </div>
      )}

      {/* Pausiert-Anzeige wenn NICHT im Edit-Modus */}
      {!isEditMode && isPausiert && (
        <div className="bg-orange-100 border-b border-orange-300 px-3 py-1">
          <p className="text-xs font-semibold text-orange-700">⏸️ Pausiert</p>
        </div>
      )}

      <div className={`p-5 ${
        isPausiert && isEditMode
          ? '[&>*:not(.no-grayscale)]:opacity-50 [&>*:not(.no-grayscale)]:grayscale'
          : isPausiert && !isEditMode
            ? 'opacity-50 grayscale'
            : ''
      }`}>

        {/* ===== IMMER SICHTBAR ===== */}

        {/* Header: Name + Badges + Expand/Collapse Button */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1 min-w-0">
            {/* Nummer + Name + Stufe */}
            <div className="flex items-center gap-2 mb-1.5 flex-wrap">
              {tp.displayNummer && (
                <span className={`flex items-center justify-center min-w-[2.5rem] h-8 px-2 rounded-full font-bold text-xs ${
                  tp.typ === "kaltkontakt"
                    ? "bg-slate-200 text-slate-700"
                    : tp.typ === "kunde"
                    ? "bg-blue-200 text-blue-800"
                    : "bg-emerald-200 text-emerald-800"
                }`}>
                  {tp.typ === "kaltkontakt" ? `#NK${tp.displayNummer}` : `#${tp.displayNummer}`}
                </span>
              )}
              <h3
                className={`text-xl font-bold text-slate-800 group-hover:text-emerald-600 transition-colors truncate ${
                  isEditMode ? 'cursor-pointer hover:bg-yellow-100 px-2 py-1 rounded-md border-2 border-dashed border-yellow-400' : ''
                }`}
                onClick={isEditMode ? () => onFieldClick('name', 'Name', 'text') : undefined}
              >
                {tp.name}
              </h3>
              {tp.typ !== "kaltkontakt" && (
                <span className={`px-2.5 py-1 text-xs font-bold rounded-full flex-shrink-0 ${
                  tp.typ === "kunde"
                    ? "bg-blue-200 text-blue-800"
                    : "bg-emerald-200 text-emerald-800"
                }`}>
                  {tp.typ === "kunde" ? "👤 Kunde" : tp.stufe}
                </span>
              )}
            </div>
            {/* Typ + kontaktiert + linie */}
            <div className="flex items-center gap-2 flex-wrap">
              {/* Typ-Badge: immer für kaltkontakt, nur im Edit-Modus für andere Typen */}
              {(tp.typ === "kaltkontakt" || isEditMode) && (
                <span
                  className={`inline-flex items-center px-2.5 py-1 text-xs font-semibold rounded-full ${
                    tp.typ === "kaltkontakt"
                      ? "bg-slate-100 text-slate-700"
                      : tp.typ === "kunde"
                      ? "bg-blue-100 text-blue-700"
                      : "bg-emerald-100 text-emerald-700"
                  } ${isEditMode ? 'cursor-pointer hover:bg-yellow-100 border-2 border-dashed border-yellow-400' : ''}`}
                  onClick={isEditMode ? () => {
                    const typOptions = tp.typ === "kaltkontakt"
                      ? ['kaltkontakt', 'teampartner', 'kunde']
                      : ['teampartner', 'kunde'];
                    onFieldClick('typ', 'Typ', 'select', typOptions);
                  } : undefined}
                >
                  {tp.typ === "kaltkontakt" && "🆕 Neuer Kontakt"}
                  {tp.typ === "teampartner" && "Teampartner"}
                  {tp.typ === "kunde" && "Kunde"}
                </span>
              )}

              {tp.typ === "kaltkontakt" && tp.kontaktiert === false && (
                <span
                  className={`inline-flex items-center px-2.5 py-1 text-xs font-semibold rounded-full bg-amber-100 text-amber-800 border border-amber-300 ${isEditMode ? 'cursor-pointer hover:bg-amber-200' : ''}`}
                  onClick={isEditMode ? () => onFieldClick('kontaktiert', 'Kontaktiert', 'checkbox') : undefined}
                >
                  ⏳ Noch nicht kontaktiert
                </span>
              )}

              {(tp.kontaktiert !== false || tp.typ !== "kaltkontakt") && !isEditMode && (tp.linie && tp.linie !== "erstlinie") && (
                <span
                  className="text-xs text-slate-500"
                >
                  {tp.linie && tp.linie !== "erstlinie" && linieName ? `🔗 ${linieName}` : null}
                </span>
              )}
            </div>

            {/* Telefon – unter den Badges (Kontakttyp/"Noch nicht kontaktiert") */}
            {(localPhone || isEditMode) && (
              <div className="mt-2">
                {isEditMode ? (
                  <button
                    type="button"
                    onClick={e => { e.stopPropagation(); setPhoneInput(localPhone); setEditingPhone(true); }}
                    className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-xl bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 text-emerald-700 font-medium transition-colors"
                  >
                    <Phone className="w-3.5 h-3.5" />
                    <span>{localPhone || 'Telefonnummer hinzufügen'}</span>
                  </button>
                ) : localPhone ? (
                  <a
                    href={`tel:${localPhone}`}
                    onClick={e => e.stopPropagation()}
                    className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-xl bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 text-emerald-700 font-medium transition-colors"
                    title={localPhone}
                  >
                    <Phone className="w-3.5 h-3.5" />
                    <span>{localPhone}</span>
                  </a>
                ) : null}
              </div>
            )}
          </div>

          {/* Phone Edit Modal */}
          {editingPhone && (
            <div
              className="fixed inset-0 bg-black/50 flex items-end z-[600]"
              onClick={e => { e.stopPropagation(); setEditingPhone(false); }}
            >
              <div
                className="bg-white w-full rounded-t-2xl px-5 pb-10 pt-5 shadow-2xl"
                onClick={e => e.stopPropagation()}
              >
                <div className="w-10 h-1 bg-slate-200 rounded-full mx-auto mb-4" />
                <h3 className="text-base font-bold text-slate-800 mb-1">Telefonnummer</h3>
                <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 mb-4">
                  <span className="text-base leading-none flex-shrink-0">📱</span>
                  <span className="text-xs text-amber-800">Wird nur auf diesem Gerät gespeichert – nicht in der Cloud.</span>
                </div>
                <input
                  autoFocus
                  type="tel"
                  value={phoneInput}
                  onChange={e => setPhoneInput(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') saveLocalPhone(phoneInput); if (e.key === 'Escape') setEditingPhone(false); }}
                  className="w-full border border-slate-200 rounded-2xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 mb-3"
                  placeholder="Telefonnummer eingeben..."
                />
                <button
                  type="button"
                  onClick={async () => {
                    if (!('contacts' in navigator) || !('ContactsManager' in window)) {
                      alert("Aus Telefonbuch auswählen ist nur in Chrome / Android verfügbar.");
                      return;
                    }
                    try {
                      const contacts = await navigator.contacts.select(['name', 'tel'], { multiple: false });
                      if (contacts && contacts.length) {
                        const phone = (contacts[0].tel && contacts[0].tel[0]) || "";
                        if (phone) setPhoneInput(phone);
                      }
                    } catch { /* User abgebrochen */ }
                  }}
                  className="w-full mb-4 py-2.5 rounded-2xl bg-blue-50 hover:bg-blue-100 text-blue-700 text-sm font-medium flex items-center justify-center gap-2 border border-blue-200"
                >
                  <Users2 className="w-4 h-4" />
                  Aus Telefonbuch auswählen
                </button>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setEditingPhone(false)}
                    className="flex-1 py-3 rounded-2xl border border-slate-200 text-sm text-slate-600 font-medium"
                  >
                    Abbrechen
                  </button>
                  <button
                    type="button"
                    onClick={() => saveLocalPhone(phoneInput)}
                    className="flex-1 py-3 rounded-2xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-sm transition-colors"
                  >
                    Speichern
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Expand/Collapse Button - nur im Normalmodus und wenn erlaubt */}
          {!isEditMode && showChevron && (
            <div className="flex items-center gap-1 flex-shrink-0 mt-0.5">
              <button
                type="button"
                data-tour="kachel-expand-button"
                data-expanded={isExpanded}
                onClick={(e) => { e.stopPropagation(); setIsExpanded(prev => !prev); }}
                title={isExpanded ? "Zuklappen" : "Ausklappen"}
                className="ml-1 p-1.5 rounded-lg hover:bg-slate-100 active:bg-slate-200 transition-colors"
              >
                {isExpanded
                  ? <ChevronUp className="w-5 h-5 text-slate-400" />
                  : <ChevronDown className="w-5 h-5 text-slate-400" />
                }
              </button>
            </div>
          )}
        </div>

        {/* Nächster Termin - immer sichtbar */}
        {(tp.naechster_termin || isEditMode) && (
          <div
            data-tour="naechster-termin-kachel"
            className={`flex items-start gap-2 p-2 rounded-lg border mb-2 ${nextTerminColor} ${isEditMode ? 'cursor-pointer hover:bg-yellow-100 border-2 border-dashed border-yellow-400' : ''}`}
            onClick={isEditMode ? () => onFieldClick('naechster_termin', 'Nächster Termin', 'naechster-termin-modal') : undefined}
          >
            <span className="text-base">{nextTerminIcon}</span>
            <div className="flex-1">
              <p className="text-xs font-medium">
                {tp.typ === "kaltkontakt" && tp.kontaktiert === false
                  ? "Erste Kontaktaufnahme"
                  : tp.typ === "kaltkontakt" && !tp.letzter_termin
                    ? "Erster Termin"
                    : "Nächster Termin"}
              </p>
              {tp.naechster_termin ? (
                <p className="text-sm font-semibold">
                  {(() => {
                    const terminDate = new Date(tp.naechster_termin);
                    const hasTime = tp.naechster_termin.includes('T');
                    if (hasTime) {
                      return (
                        <>
                          {terminDate.toLocaleDateString('de-DE', { day: '2-digit', month: 'short', year: 'numeric' })}
                          <span className="ml-2">um {terminDate.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })} Uhr</span>
                        </>
                      );
                    } else {
                      return terminDate.toLocaleDateString('de-DE', { day: '2-digit', month: 'short', year: 'numeric' });
                    }
                  })()}
                  {nextTerminStatus && <span className="ml-2">• {nextTerminStatus}</span>}
                </p>
              ) : (
                <p className="text-xs text-yellow-600 italic">Klicken zum Bearbeiten</p>
              )}
            </div>
          </div>
        )}

        {/* Nächstes Thema - immer sichtbar (Normalmodus-Version) */}
        {!isEditMode && (
          <div className="bg-slate-50 rounded-lg px-3 py-2 border border-slate-200 mb-3">
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-slate-600">Nächstes Thema:</p>
                {tp.naechstes_thema ? (
                  <p className="text-xs text-slate-800 break-words whitespace-pre-wrap">{tp.naechstes_thema}</p>
                ) : (
                  <p className="text-xs text-slate-400 italic">Kein Thema gesetzt</p>
                )}
              </div>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  if (onShowVergangeneThemen) onShowVergangeneThemen(tp);
                }}
                className="text-blue-600 hover:text-blue-800 flex-shrink-0 mt-0.5"
                title="Vergangene Themen"
              >
                📋
              </button>
            </div>
          </div>
        )}

        {/* Action Buttons im eingeklappten Zustand */}
        {!showFull && !isEditMode && (
          <div className="flex gap-2 pt-3 mt-1 border-t border-slate-100">
            {onEdit && (
              <Button
                size="sm"
                onClick={(e) => { e.stopPropagation(); onEdit(tp); }}
                className="flex-1 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white"
              >
                <Edit className="w-3.5 h-3.5 mr-1.5" />
                Bearbeiten
              </Button>
            )}
            {!isKaltkontakt && tp.typ === "teampartner" && onPromote && (
              <Button
                size="sm"
                variant="outline"
                onClick={(e) => { e.stopPropagation(); onPromote(tp); }}
                className="text-purple-600 hover:text-purple-700 hover:bg-purple-50 border-purple-200"
              >
                🚀
              </Button>
            )}
            <Button
              size="sm"
              variant="outline"
              onClick={(e) => { e.stopPropagation(); onDelete(tp.id); }}
              className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </div>
        )}

        {/* ===== AUSGEKLAPPT / EDIT-MODUS ===== */}
        {showFull && (
          <>
            {/* Divider */}
            <div className="h-px bg-slate-200 mb-4" />

            {/* Details-Bereich */}
            <div className="space-y-2.5 mb-4">
              {/* Nächstes Thema - Edit-Modus Version */}
              {isEditMode && (
                <div className="flex flex-col gap-1">
                  <div
                    className="flex items-start gap-2 p-2 rounded-lg border border-slate-200 cursor-pointer hover:bg-yellow-100 border-2 border-dashed border-yellow-400"
                    onClick={() => onFieldClick('naechstes_thema', 'Nächstes Thema', 'text')}
                  >
                    <span className="text-base">📝</span>
                    <div className="flex-1">
                      <p className="text-xs font-medium">Nächstes Thema</p>
                      {tp.naechstes_thema ? (
                        <p className="text-sm font-semibold">{tp.naechstes_thema}</p>
                      ) : (
                        <p className="text-xs text-yellow-600 italic">Klicken zum Bearbeiten</p>
                      )}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onShowVergangeneThemen(tp);
                    }}
                    className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1 self-end"
                  >
                    <History className="w-3 h-3" />
                    Vergangene Themen
                  </button>
                </div>
              )}

              {/* Zuletzt kontaktiert */}
              {(tp.kontaktiert !== false || tp.typ !== "kaltkontakt") && (tp.letzter_termin || isEditMode) && (
                <div data-tour="letzter-termin-kachel">
                  <div
                    className={`flex items-start gap-2 p-2 rounded-lg border ${lastContactColor} ${isEditMode ? 'cursor-pointer hover:bg-yellow-100 border-2 border-dashed border-yellow-400' : ''}`}
                    onClick={isEditMode ? () => onFieldClick('letzter_termin', 'Letzter Termin', 'date') : undefined}
                  >
                    <span className="text-base">{lastContactIcon}</span>
                    <div className="flex-1">
                      <div className="flex items-center gap-1">
                        <p className="text-xs font-medium">Zuletzt kontaktiert</p>
                        {!isEditMode && (
                          <button
                            type="button"
                            onClick={e => { e.stopPropagation(); setShowContactInfo(v => !v); }}
                            className="ml-auto text-current opacity-60 hover:opacity-100 transition-opacity"
                            data-testid="contact-info-toggle"
                          >
                            <Info className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                      {tp.letzter_termin ? (
                        <p className="text-sm font-semibold">
                          {new Date(tp.letzter_termin).toLocaleDateString('de-DE', { day: '2-digit', month: 'short', year: 'numeric' })}
                          <span className="ml-2">• {lastContactText}</span>
                        </p>
                      ) : (
                        <p className="text-xs text-yellow-600 italic">Klicken zum Bearbeiten</p>
                      )}
                    </div>
                  </div>
                  {/* Info-Panel: Farbgebung & Nachrichtencenter-Logik */}
                  {showContactInfo && !isEditMode && (
                    <div className="mt-1 p-2.5 rounded-lg bg-slate-50 border border-slate-200 text-xs text-slate-600 space-y-1.5">
                      <p className="font-semibold text-slate-700 mb-1">Farbgebung & Nachrichtencenter</p>
                      <div className="flex items-center gap-2">
                        <span className="w-2.5 h-2.5 rounded-full bg-green-500 flex-shrink-0" />
                        <span><strong>Grün</strong> – Kontakt innerhalb der letzten {yellowDays !== null ? yellowDays : redDays ?? "∞"} Tage: alles gut</span>
                      </div>
                      {yellowDays !== null ? (
                        <div className="flex items-center gap-2">
                          <span className="w-2.5 h-2.5 rounded-full bg-yellow-400 flex-shrink-0" />
                          <span><strong>Gelb</strong> – ab {yellowDays + 1} Tagen: im Nachrichtencenter als Erinnerung sichtbar</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <span className="w-2.5 h-2.5 rounded-full bg-yellow-400 flex-shrink-0 opacity-30" />
                          <span className="opacity-50"><strong>Gelb</strong> – deaktiviert</span>
                        </div>
                      )}
                      {redDays !== null ? (
                        <div className="flex items-center gap-2">
                          <span className="w-2.5 h-2.5 rounded-full bg-red-500 flex-shrink-0" />
                          <span><strong>Rot</strong> – ab {redDays + 1} Tagen: dringend – wird im Nachrichtencenter hervorgehoben</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <span className="w-2.5 h-2.5 rounded-full bg-red-500 flex-shrink-0 opacity-30" />
                          <span className="opacity-50"><strong>Rot</strong> – deaktiviert</span>
                        </div>
                      )}
                      <p className="text-slate-400 pt-1 border-t border-slate-100">Anpassbar unter Profil → Kontakt-Erinnerungen</p>
                    </div>
                  )}
                </div>
              )}

              {/* Linie – im Edit-Modus nach Zuletzt kontaktiert */}
              {isEditMode && (
                <div
                  className="flex items-start gap-2 p-2 rounded-lg border border-slate-200 cursor-pointer hover:bg-yellow-100 border-2 border-dashed border-yellow-400"
                  onClick={() => {
                    axios.get(`${API}/api/teampartner`).then(res => {
                      const teampartnerOptions = ['erstlinie', ...res.data.map(t => t.id)];
                      const teampartnerLabels = ['Keiner', ...res.data.map(t => t.name)];
                      onFieldClick('linie', 'Linie', 'select', teampartnerOptions, teampartnerLabels);
                    });
                  }}
                >
                  <span className="text-base">🔗</span>
                  <div className="flex-1">
                    <p className="text-xs font-medium">Linie</p>
                    <p className="text-sm font-semibold">
                      {tp.linie === "erstlinie" || !tp.linie ? "Erstlinie" : (linieName || "Klicken zum Bearbeiten")}
                    </p>
                  </div>
                </div>
              )}

              {/* Noch zu erledigen */}
              {(normalizeNochZuErledigen(tp.noch_zu_erledigen).length > 0 || isEditMode) && (
                <div className="p-2 rounded-lg border border-slate-200">
                  <p className="text-xs text-slate-500 font-medium mb-1.5">📝 Zu erledigen</p>
                  <KontaktTaskList
                    tasks={normalizeNochZuErledigen(tp.noch_zu_erledigen)}
                    isEditMode={isEditMode}
                    compact={!isEditMode}
                    onToggle={(taskId) => onToggleTask && onToggleTask(tp.id, tp.typ, taskId)}
                    onDelete={(taskId) => onDeleteTask && onDeleteTask(tp.id, tp.typ, taskId)}
                    onAdd={() => onOpenZuErledigen && onOpenZuErledigen(tp.id, tp.name, tp.typ, false)}
                  />
                </div>
              )}

              {/* Notes Preview */}
              {(tp.notizen || isEditMode) && (
                <div
                  className={`flex items-start gap-2 bg-slate-50 p-2 rounded-lg ${isEditMode ? 'cursor-pointer hover:bg-yellow-100 border-2 border-dashed border-yellow-400' : ''}`}
                  onClick={isEditMode ? () => onFieldClick('notizen', 'Notizen', 'textarea') : undefined}
                >
                  <span className="text-base flex-shrink-0">💭</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-slate-500 font-medium">Notizen</p>
                    {tp.notizen ? (
                      <p className="text-xs text-slate-600 whitespace-pre-wrap break-words max-h-32 overflow-y-auto pr-1">{tp.notizen}</p>
                    ) : (
                      <p className="text-xs text-yellow-600 italic">Klicken zum Bearbeiten</p>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Status Badges */}
            <div className="flex flex-wrap gap-2 mb-4">
              {tp.termin_hatte && (
                <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full font-medium">
                  ✓ Termin gehabt
                </span>
              )}
            </div>

            {/* Downline Info (für Teampartner) */}
            {!isKaltkontakt && downlineCount && (
              <div
                className={`mb-3 p-2 bg-blue-50 rounded-lg border border-blue-200 ${onShowDownlineTree ? 'cursor-pointer hover:bg-blue-100' : ''} transition-colors`}
                onClick={onShowDownlineTree ? () => onShowDownlineTree(tp) : undefined}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-blue-700">📊 Downline</span>
                    <span className="text-xs text-blue-600">
                      {downlineCount.total} Gesamt ({downlineCount.tp_count} TP)
                    </span>
                  </div>
                  {tp.stufe === "TP" && downlineCount.tp_count >= 5 && (
                    <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded-full font-bold animate-pulse">
                      🎉 Auto-M fähig
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Betreuungssystem Anzeige */}
            {!isKaltkontakt && (
              <div
                className={`mb-3 p-3 bg-indigo-50 rounded-lg border border-indigo-200 ${
                  !isEditMode && onBetreuungClick && !onBetreuungToggle
                    ? 'cursor-pointer hover:bg-indigo-100 hover:border-indigo-300 transition-colors active:bg-indigo-200'
                    : ''
                }`}
                onClick={!isEditMode && onBetreuungClick && !onBetreuungToggle
                  ? (e) => { e.stopPropagation(); onBetreuungClick(tp); }
                  : undefined}
              >
                <p className="text-xs font-semibold text-indigo-700 mb-2">
                  📋 Betreuung
                  {!isEditMode && onBetreuungClick && !onBetreuungToggle && (
                    <span className="ml-2 text-indigo-400 font-normal text-xs">• tippen zum bearbeiten</span>
                  )}
                </p>

                {followupStatus && (
                  <>
                    {(() => {
                      const faelligeFollowups = followupStatus.followups.filter(fu => {
                        if (fu.label === "Paket Erinnerung" && !fu.isActive) return false;
                        if (fu.needsEinnahme && !followupStatus.hasEinnahme) return false;
                        return !fu.done && fu.daysSince >= fu.days;
                      });
                      const kommendeFollowup = followupStatus.followups.find(fu => {
                        if (fu.label === "Paket Erinnerung" && !fu.isActive) return false;
                        if (fu.needsEinnahme && !followupStatus.hasEinnahme) return false;
                        return !fu.done && fu.daysSince < fu.days;
                      });
                      if (faelligeFollowups.length > 0) {
                        const texts = faelligeFollowups.map(fu => {
                          if (fu.label === "Paket Erinnerung") return "⏰ Paket Erinnerung fällig!";
                          return fu.label.replace(" (7T)", "") + " fällig!";
                        });
                        return <p className="text-xs text-indigo-600 mb-1 font-semibold">{texts.join(" • ")}</p>;
                      }
                      if (kommendeFollowup) {
                        const daysUntilNext = kommendeFollowup.days - kommendeFollowup.daysSince;
                        let text = '';
                        if (kommendeFollowup.label === "Einnahmegespräch (7T)") text = `${daysUntilNext} Tage bis zum Einnahmegespräch`;
                        else if (kommendeFollowup.label === "3-5 Tage FU") text = `3-5 Tage bis zum nächsten Gespräch`;
                        else if (kommendeFollowup.label === "14 Tage FU") text = `14 Tage bis zum nächsten Gespräch`;
                        else if (kommendeFollowup.label === "30 Tage FU") text = `30 Tage bis zum nächsten Gespräch`;
                        else if (kommendeFollowup.label === "Paket Erinnerung") text = `⏳ Paket Erinnerung in ${daysUntilNext} Tagen`;
                        return <p className="text-xs text-indigo-600 mb-1">{text}</p>;
                      }
                      const alleDone = followupStatus.followups.filter(f => f.done).length === followupStatus.followups.length;
                      return (
                        <p className="text-xs text-indigo-600 mb-1 font-semibold">
                          {alleDone ? '😊 Individuelle Betreuung' : 'Warten auf nächstes Follow-up'}
                        </p>
                      );
                    })()}
                    <div className="flex items-center gap-1 mb-2 flex-wrap">
                      {followupStatus.followups.map((fu, idx) => {
                        const fieldName = fu.fieldName || '';
                        const isClickable = (isEditMode && fieldName) || (!isEditMode && onBetreuungToggle && fieldName);
                        return (
                          <span
                            key={idx}
                            className={`text-lg ${isClickable ? 'cursor-pointer hover:scale-125 transition-transform' : ''}`}
                            title={fu.label}
                            onClick={
                              isEditMode && fieldName
                                ? () => onFieldClick(fieldName, fu.label, 'checkbox')
                                : (!isEditMode && onBetreuungToggle && fieldName)
                                  ? (e) => { e.stopPropagation(); onBetreuungToggle(tp, fieldName); }
                                  : undefined
                            }
                          >
                            {fu.done
                              ? '✅'
                              : (fu.needsEinnahme && !followupStatus.hasEinnahme)
                                ? '⏳'
                                : (fu.daysSince >= fu.days)
                                  ? '⏰'
                                  : '⏳'}
                          </span>
                        );
                      })}
                      <span className="text-xs text-indigo-600 ml-2">
                        ({followupStatus.followups.filter(f => f.done).length}/{followupStatus.followups.length})
                      </span>
                    </div>
                  </>
                )}

                {!followupStatus && (
                  <div className="flex items-center gap-1 mb-2">
                    <span className="text-lg">⏳</span>
                    <span className="text-lg">⏳</span>
                    <span className="text-lg">⏳</span>
                    <span className="text-lg">⏳</span>
                    <span className="text-lg">⏳</span>
                    <span className="text-xs text-indigo-600 ml-2">(0/5)</span>
                  </div>
                )}

                <p
                  className={`text-xs text-indigo-600 ${isEditMode ? 'cursor-pointer hover:bg-yellow-100 p-2 rounded-md border-2 border-dashed border-yellow-400' : ''}`}
                  onClick={isEditMode ? () => onFieldClick('cellreset_gemacht', 'Cellreset', 'select', ['nein', 'interesse', 'ja', 'nicht_beendet'], ['Nein', 'Interesse', 'Ja', 'Nicht beendet']) : undefined}
                >
                  🧬 Cellreset gemacht?: <span className="font-semibold">
                    {tp.cellreset_gemacht === "ja" ? "✓ Ja" : tp.cellreset_gemacht === "interesse" ? "🤔 Interesse" : tp.cellreset_gemacht === "nicht_beendet" ? "⏸️ Nicht beendet" : "Nein"}
                  </span>
                </p>

                {/* kein weiterer Text nötig – Emojis zeigen den Status */}
              </div>
            )}

            {/* Nächstes Paket */}
            {!isKaltkontakt && (
              <div
                data-field="naechstes-paket"
                className={`mb-3 p-2 rounded-lg border bg-teal-50 border-teal-200 ${isEditMode ? 'cursor-pointer hover:bg-yellow-100 border-2 border-dashed border-yellow-400' : ''}`}
                onClick={isEditMode ? () => onFieldClick('befoerderungs_datum', 'Nächstes Paket - Einschreibedatum', 'date') : undefined}
              >
                <div className="flex items-start gap-2">
                  <span className="text-sm">📦</span>
                  <div className="flex-1">
                    <p className="text-xs font-medium text-slate-600">Nächstes Paket:</p>
                    {tp.befoerderungs_datum ? (() => {
                      const { nextRenewal, completedCycles, daysLeft } = calculateNextPackage(tp.befoerderungs_datum);
                      return (
                        <p className="text-xs font-normal text-slate-700">
                          {nextRenewal.toLocaleDateString('de-DE', { day: '2-digit', month: 'short', year: 'numeric' })}
                          <span className={`ml-1 font-semibold ${daysLeft <= 7 ? 'text-orange-600' : 'text-teal-600'}`}>
                            • {daysLeft === 0 ? 'Heute fällig!' : daysLeft === 1 ? 'Morgen fällig!' : `in ${daysLeft} Tagen`}
                          </span>
                          {completedCycles > 0 && <span className="ml-1 text-xs text-slate-400">({completedCycles + 1}. Zyklus)</span>}
                        </p>
                      );
                    })() : (
                      <p className="text-xs text-slate-500 italic">
                        {isEditMode ? 'Klicken zum Festlegen' : 'Noch nicht eingeschrieben'}
                      </p>
                    )}
                    {isEditMode && !tp.befoerderungs_datum && (
                      <p className="text-xs text-red-600 font-semibold mt-1">⚠️ Pflichtfeld: Bitte Einschreibedatum festlegen!</p>
                    )}
                    {isEditMode && tp.befoerderungs_datum && (
                      <p className="text-xs text-yellow-600 italic mt-1">Klicken um neues Einschreibedatum zu setzen (startet Follow-ups neu)</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Thematik & Produkte */}
            {(tp.thematik?.length > 0 || tp.actionpack !== "nein" || tp.im_abo?.length > 0 || tp.produktempfehlung?.length > 0 || isEditMode) && (
              <div className="mb-3 space-y-2">
                {/* Thematik */}
                {(tp.thematik && tp.thematik.length > 0) || isEditMode ? (
                  <div
                    className={`p-2 bg-purple-50 rounded-lg border border-purple-200 ${isEditMode ? 'cursor-pointer hover:bg-yellow-100 border-2 border-dashed border-yellow-400' : ''}`}
                    onClick={isEditMode ? () => {
                      const allThematik = [...DEFAULT_THEMATIK_OPTIONEN, ...(customThematikOptionen || [])];
                      onFieldClick('thematik', 'Thematik', 'multiselect-with-manager', allThematik);
                    } : undefined}
                  >
                    <p className="text-xs font-semibold text-purple-700 mb-1">🏥 Thematik</p>
                    {tp.thematik && tp.thematik.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {tp.thematik.map((thema, idx) => (
                          <span key={idx} className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded text-xs">{thema}</span>
                        ))}
                      </div>
                    ) : isEditMode ? (
                      <p className="text-xs text-yellow-600 italic">Klicken zum Bearbeiten</p>
                    ) : null}
                  </div>
                ) : null}

                {/* Online/Live Veranstaltungen */}
                {((tp.online_live_veranstaltungen && tp.online_live_veranstaltungen.length > 0) || isEditMode) && (
                  <div
                    className={`p-1.5 bg-blue-50 rounded-lg border border-blue-200 ${isEditMode ? 'cursor-pointer hover:bg-yellow-100 border-2 border-dashed border-yellow-400' : ''}`}
                    onClick={isEditMode ? () => {
                      const allVeranstaltungen = [...DEFAULT_VERANSTALTUNG_OPTIONEN, ...(customVeranstaltungOptionen || [])];
                      onFieldClick('online_live_veranstaltungen', 'Veranstaltungen', 'multiselect-with-manager', allVeranstaltungen);
                    } : undefined}
                  >
                    <p className="text-xs font-medium text-blue-700 mb-1">🎯 Teilnahmen</p>
                    {tp.online_live_veranstaltungen && tp.online_live_veranstaltungen.length > 0 ? (
                      <div className="flex flex-wrap gap-0.5">
                        {tp.online_live_veranstaltungen.map((veranstaltung, idx) => (
                          <span key={idx} className="px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">{veranstaltung}</span>
                        ))}
                      </div>
                    ) : isEditMode ? (
                      <p className="text-xs text-yellow-600 italic">Klicken zum Bearbeiten</p>
                    ) : null}
                  </div>
                )}

                {/* Produkte im Abo */}
                {!isKaltkontakt && ((tp.im_abo && tp.im_abo.length > 0) || isEditMode) && (
                  <div
                    className={`p-2 bg-green-50 rounded-lg border border-green-200 ${isEditMode ? 'cursor-pointer hover:bg-yellow-100 border-2 border-dashed border-yellow-400' : ''}`}
                    onClick={isEditMode ? () => {
                      const produktOptions = ['konzept-1', ...produkte.map(p => p.id)];
                      const produktLabels = ['Konzept 1', ...produkte.map(p => p.name)];
                      onFieldClick('im_abo', 'Im Abo', 'multiselect', produktOptions, produktLabels);
                    } : undefined}
                  >
                    <p className="text-xs font-semibold text-green-700 mb-1">✓ Im Abo ({tp.im_abo?.length || 0})</p>
                    {tp.im_abo && tp.im_abo.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {tp.im_abo.map(produktId => {
                          if (produktId === "konzept-1") return <span key={produktId} className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs font-semibold">Konzept 1</span>;
                          const produkt = produkte.find(p => p.id === produktId);
                          return produkt ? <span key={produktId} className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs">{produkt.name}</span> : null;
                        })}
                      </div>
                    ) : isEditMode ? <p className="text-xs text-yellow-600 italic">Klicken zum Bearbeiten</p> : null}
                  </div>
                )}

                {/* Produktempfehlungen */}
                {!isKaltkontakt && ((tp.produktempfehlung && tp.produktempfehlung.length > 0) || isEditMode) && (
                  <div
                    className={`p-2 bg-yellow-50 rounded-lg border border-yellow-200 ${isEditMode ? 'cursor-pointer hover:bg-yellow-100 border-2 border-dashed border-yellow-400' : ''}`}
                    onClick={isEditMode ? () => {
                      const verfuegbareProdukte = produkte.filter(p => !tp.im_abo?.includes(p.id));
                      const hasKonzept1 = !tp.im_abo?.includes('konzept-1');
                      const produktOptions = hasKonzept1 ? ['konzept-1', ...verfuegbareProdukte.map(p => p.id)] : verfuegbareProdukte.map(p => p.id);
                      const produktLabels = hasKonzept1 ? ['Konzept 1', ...verfuegbareProdukte.map(p => p.name)] : verfuegbareProdukte.map(p => p.name);
                      onFieldClick('produktempfehlung', 'Produktempfehlungen', 'multiselect', produktOptions, produktLabels);
                    } : undefined}
                  >
                    <p className="text-xs font-semibold text-yellow-700 mb-1">💡 Empfehlungen ({tp.produktempfehlung?.length || 0})</p>
                    {tp.produktempfehlung && tp.produktempfehlung.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {tp.produktempfehlung.map(produktId => {
                          if (produktId === "konzept-1") return <span key={produktId} className="px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded text-xs font-semibold">Konzept 1</span>;
                          const produkt = produkte.find(p => p.id === produktId);
                          return produkt ? <span key={produktId} className="px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded text-xs">{produkt.name}</span> : null;
                        })}
                      </div>
                    ) : isEditMode ? <p className="text-xs text-yellow-600 italic">Klicken zum Bearbeiten</p> : null}
                  </div>
                )}
              </div>
            )}

            {/* Actionpack */}
            {(tp.kontaktiert !== false || tp.typ !== "kaltkontakt") && !isKaltkontakt && (
              <div
                className={`mb-3 p-2 bg-orange-50 rounded-lg border border-orange-200 ${isEditMode ? 'cursor-pointer hover:bg-yellow-100 border-2 border-dashed border-yellow-400' : ''}`}
                onClick={isEditMode ? () => onFieldClick('actionpack', 'Actionpack', 'select', ['nein', 'interesse', 'ja'], ['Nein', 'Interesse', 'Ja']) : undefined}
              >
                <p className="text-xs font-semibold text-orange-700">
                  📦 Actionpack genutzt?: <span className="font-bold">{tp.actionpack === "ja" ? "✓ Ja" : tp.actionpack === "interesse" ? "🤔 Interesse" : "Nein"}</span>
                </p>
              </div>
            )}

            {/* Schulden */}
            {(() => {
              let personSchulden = alleSchulden.filter(s =>
                s.status === "offen" && (s.name === tp.name || s.teampartner_id === tp.id)
              );
              if (isEditMode && tempBezahlteSchulden && Array.isArray(tempBezahlteSchulden) && tempBezahlteSchulden.length > 0) {
                personSchulden = personSchulden.filter(s => !tempBezahlteSchulden.includes(s.id));
              }
              if (isEditMode && tempNeueSchulden && Array.isArray(tempNeueSchulden) && tempNeueSchulden.length > 0) {
                const neueSchuldenFormatiert = tempNeueSchulden.map((schuld, idx) => ({
                  id: `temp-${idx}`, ...schuld, betrag: parseFloat(schuld.betrag) || 0, status: "offen", isTemp: true
                }));
                personSchulden = [...personSchulden, ...neueSchuldenFormatiert];
              }
              if (personSchulden.length === 0 && !isEditMode) return null;
              const erhalten = personSchulden.filter(s => s.richtung === "erhalten_von").reduce((sum, p) => sum + p.betrag, 0);
              const schulde = personSchulden.filter(s => s.richtung === "schulde_ich").reduce((sum, p) => sum + p.betrag, 0);
              const bilanz = erhalten - schulde;
              const tempNeueAnzahl = personSchulden.filter(s => s.isTemp).length;

              // Teilzahlungen-Summe berechnen
              const teilzahlungenSumme = personSchulden.reduce((s, p) => {
                const tz = (p.teilzahlungen || []).reduce((a, t) => a + (parseFloat(t.betrag) || 0), 0);
                return s + tz;
              }, 0);
              const hatTeilzahlungen = personSchulden.some(p => (p.teilzahlungen || []).length > 0);
              const offeneCount = personSchulden.filter(p => !p.isTemp && p.status !== "bezahlt").length;
              return (
                <div className="mb-3 p-3 bg-gradient-to-br from-purple-50 to-indigo-50 rounded-lg border border-purple-200 shadow-sm no-grayscale">
                  <div className="flex justify-between items-center mb-2">
                    <div className="flex items-center gap-2">
                      <p className="text-xs font-bold text-slate-800 flex items-center gap-1">
                        💰 Schulden {personSchulden.length > 0 && `(${personSchulden.length} Posten)`}
                      </p>
                      {tempNeueAnzahl > 0 && (
                        <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-semibold rounded">+{tempNeueAnzahl} neu</span>
                      )}
                    </div>
                    {personSchulden.length > 0 && (
                      <span className={`text-sm font-bold px-2 py-0.5 rounded ${bilanz >= 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                        {bilanz >= 0 ? '+' : ''}{bilanz.toFixed(2)} €
                      </span>
                    )}
                  </div>
                  {personSchulden.length > 0 && (
                    <div className="space-y-1">
                      {erhalten > 0 && <p className="text-xs text-green-700">→ Schuldet mir: <span className="font-semibold">{erhalten.toFixed(2)} €</span></p>}
                      {schulde > 0 && <p className="text-xs text-red-700">← Ich schulde: <span className="font-semibold">{schulde.toFixed(2)} €</span></p>}
                      {teilzahlungenSumme > 0 && (
                        <p className="text-xs text-amber-700">
                          💵 Bereits teilbezahlt: <span className="font-semibold">{teilzahlungenSumme.toFixed(2)} €</span>
                        </p>
                      )}
                    </div>
                  )}
                  {personSchulden.length > 0 && (() => {
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    const ueberfaellig = personSchulden.filter(s => {
                      const faellig = new Date(s.faelligkeitsdatum);
                      faellig.setHours(0, 0, 0, 0);
                      return faellig < today;
                    });
                    if (ueberfaellig.length > 0) {
                      return (
                        <div className="mt-2 pt-2 border-t border-purple-300">
                          <p className="text-xs font-semibold text-red-600 flex items-center gap-1">⚠️ {ueberfaellig.length} überfällig</p>
                        </div>
                      );
                    }
                  })()}

                  {/* Action Buttons (auch außerhalb Edit-Mode) */}
                  {!isEditMode && personSchulden.length > 0 && !personSchulden.some(p => p.isTemp) && (
                    <div className="mt-3 pt-3 border-t border-purple-300 flex flex-wrap gap-2">
                      {offeneCount > 0 && onPayPerson && (
                        <button
                          data-testid={`tp-card-pay-${tp.id}`}
                          onClick={(e) => { e.stopPropagation(); onPayPerson(personSchulden, tp.name); }}
                          className="flex-1 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-lg text-xs font-semibold"
                        >
                          ✓ Bezahlen
                        </button>
                      )}
                      {hatTeilzahlungen && onShowTeilzahlungHistorie && (
                        <button
                          data-testid={`tp-card-history-${tp.id}`}
                          onClick={(e) => { e.stopPropagation(); onShowTeilzahlungHistorie(personSchulden, tp.name); }}
                          className="px-3 py-1.5 bg-amber-50 hover:bg-amber-100 text-amber-700 border border-amber-300 rounded-lg text-xs font-semibold inline-flex items-center gap-1"
                        >
                          <History className="w-3 h-3" /> Teilzahlungen
                        </button>
                      )}
                    </div>
                  )}

                  {isEditMode && (
                    <div className="mt-3 pt-3 border-t border-purple-300 flex flex-wrap gap-2">
                      {personSchulden.length > 0 && (
                        <button
                          onClick={(e) => { e.stopPropagation(); if (onFieldClick) onFieldClick('schulden_bezahlen', 'Schulden bezahlen', 'schulden-bezahlen-modal', personSchulden); }}
                          className="flex-1 px-3 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-xs font-semibold"
                        >
                          ✓ Bezahlen
                        </button>
                      )}
                      <button
                        onClick={(e) => { e.stopPropagation(); if (onFieldClick) onFieldClick('neue_schulden', 'Neue Schulden', 'neue-schulden-modal', { name: tp.name, teampartner_id: tp.id }); }}
                        className="flex-1 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-semibold"
                      >
                        + Neue Schulden
                      </button>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Action Buttons im ausgeklappten Zustand */}
            <div className="flex gap-2 pt-3 border-t border-slate-200">
              {onEdit && !isEditMode && (
                <Button
                  size="sm"
                  onClick={(e) => { e.stopPropagation(); onEdit(tp); }}
                  className="flex-1 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white"
                >
                  <Edit className="w-3.5 h-3.5 mr-1.5" />
                  Bearbeiten
                </Button>
              )}
              {!isKaltkontakt && tp.typ === "teampartner" && onPromote && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={(e) => { e.stopPropagation(); onPromote(tp); }}
                  className="text-purple-600 hover:text-purple-700 hover:bg-purple-50 border-purple-200"
                >
                  🚀
                </Button>
              )}
              <Button
                size="sm"
                variant="outline"
                onClick={(e) => { e.stopPropagation(); onDelete(tp.id); }}
                className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          </>
        )}

      </div>

      {/* Hover Effect Overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-transparent via-transparent to-slate-100 opacity-0 group-hover:opacity-30 transition-opacity duration-300 pointer-events-none" />
    </div>
  );
}
